import { FC, useState, useCallback, useRef, useMemo } from 'react'
import { Skeleton } from 'antd'
import { MyCourse, MyCourseDetailed } from '@models/myCourse'
import Accordion, { AccordionBodyProps, AccordionHeaderProps } from '@molecules/Accordion'
import { ListItemProps } from '@templates/List'
import { useGetMyCourseByIdQuery, useLazyGetMyCourseByIdQuery } from '@services/myCourses'
import CourseFormModal from '@modals/CourseFormModal'
import Modal from '@organisms/Modal'
import Header from './Header'
import Body from './Body'

const MyCourseItem: FC<ListItemProps<MyCourse>> = ({
  isLoading,
  item,
  opened,
  handleClickOnItem,
  additionalPropsForItem,
}) => {
  const { id } = item
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [courseDetailed, setCourseDetailed] = useState<MyCourseDetailed | null>(
    null
  )

  const [fetchCourseById] = useLazyGetMyCourseByIdQuery()
  const { data: expandedCourseData, isLoading: isExpandedQueryLoading } =
    useGetMyCourseByIdQuery(id, { skip: !opened })

  // Карточка раскрыта, но детальные данные курса ещё не пришли → считаем, что идёт загрузка.
  // Закрывает пред-загрузочный зазор: при переключении skip RTK isLoading на один кадр бывает
  // false до старта запроса, из-за чего фраза «В курсе пока нет разделов и тем» мигала до данных.
  const isExpandedLoading =
    opened && (isExpandedQueryLoading || !expandedCourseData)

  const courseForBody = (opened ? expandedCourseData : null) ?? item

  const handleEdit = useCallback(async () => {
    const result = await fetchCourseById(id)
    if (result.data) {
      setCourseDetailed(result.data)
      setIsEditModalOpen(true)
    }
  }, [fetchCourseById, id])

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false)
    setCourseDetailed(null)
  }, [])

  // Stable component references for Accordion: inline arrow functions create a
  // new reference on every render, which React treats as a different component
  // type → unmount/remount → all internal state (expandedChapters etc.) is lost.
  // Reading from a ref keeps the function identity stable while accessing fresh data.
  const bodyDepsRef = useRef({
    courseForBody,
    handleEdit,
    showDeleted: additionalPropsForItem?.showDeleted,
    isExpandedLoading,
    opened,
  })
  bodyDepsRef.current = {
    courseForBody,
    handleEdit,
    showDeleted: additionalPropsForItem?.showDeleted,
    isExpandedLoading,
    opened,
  }

  const headerDepsRef = useRef({ handleClickOnItem, item })
  headerDepsRef.current = { handleClickOnItem, item }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const StableBody = useMemo<FC<AccordionBodyProps>>(
    () =>
      function CourseBody({ onChangeSize, toggleLoading }) {
        const d = bodyDepsRef.current
        return (
          <Body
            course={d.courseForBody as MyCourse}
            onChangeSize={onChangeSize}
            toggleLoading={toggleLoading}
            onEdit={d.handleEdit}
            showDeleted={d.showDeleted}
            isExpandedLoading={d.isExpandedLoading && d.opened}
          />
        )
      },
    []
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const StableHeader = useMemo<FC<AccordionHeaderProps>>(
    () =>
      function CourseHeader({ hovered, expanded }) {
        const d = headerDepsRef.current
        return (
          <Header
            onClick={d.handleClickOnItem}
            item={d.item}
            hovered={hovered}
            expanded={expanded}
          />
        )
      },
    []
  )

  if (isLoading) {
    return (
      <Skeleton.Button
        active
        block
        style={{
          height: 170,
          marginBottom: 12,
          borderRadius: 30,
        }}
      />
    )
  }

  return (
    <>
      <Accordion
        id={`my-course-${id}`}
        expanded={opened}
        itemId={id}
        skeletonHeight={145}
        initLoading={isLoading}
        Header={StableHeader}
        Body={StableBody}
        withoutArrow
        withAutoRefreshSize
      />
      <Modal
        visible={isEditModalOpen && courseDetailed !== null}
        onModalClose={handleCloseEditModal}
        width="calc(100vw - 90px)"
        withoutCloseOnOutsideClick
      >
        {courseDetailed && (
          <CourseFormModal
            isEdit
            course={courseDetailed}
            handleCloseModal={handleCloseEditModal}
            internalCourseMode
          />
        )}
      </Modal>
    </>
  )
}

export default MyCourseItem
